/**
 * WebSocket Streaming Client
 * Handles real-time audio streaming via WebSocket
 */

import type { AudioQualityEvent } from '@/types/audioQuality';
import type { AttributedSummaryData, RegenTriggerHint } from '@/types/summaryGeneration';
import type { SummaryStyle } from '@/types/summaryStyle';
import { type Socket, io } from 'socket.io-client';
import type { PCMChunk } from './pcmAudioManager';

export interface StreamingClientConfig {
  url?: string;
  namespace?: string;
  authToken?: string;
  /**
   * Opaque handle for a redeemed embed session.
   *
   * Sent alongside the token, never instead of it. The server binds the two and
   * refuses the pair unless both name the same clinician, organisation and
   * partner, so a handle on its own is not a credential here.
   */
  embedSession?: string;
}

export interface TranscriptionResult {
  segment_id?: string; // Unique ID for this segment (for tracking updates)
  segment_number: number;
  text: string;
  start_time: number;
  end_time: number;
  language?: string;
  is_provisional?: boolean; // True for instant feedback chunks, false for final accumulated transcription
  is_full_transcript?: boolean; // True when text is full formatted transcript (replace, don't append)
  is_pending_refinement?: boolean; // True when instant model emitted, waiting for large model refinement
}

export interface TranscriptionUpdateEvent {
  segment_id: string;
  segment_number: number;
  text?: string; // Updated text from large model (if different)
  is_pending_refinement: boolean; // False when refinement complete
  was_refined?: boolean; // True if text was changed by large model
  session_id: string;
}

/**
 * Streaming dictation (beta) hit a problem. Never carries transcript text.
 * - final_failed: ASR could not transcribe a finished utterance
 * - stream_fallback: the stream stopped for good; audio continues through the regular pipeline
 * - reconnecting: the stream is reconnecting; partial text is stale
 * audio_recovered: the affected audio was handed to the regular pipeline
 */
export interface StreamingWarningEvent {
  session_id: string;
  kind: 'final_failed' | 'stream_fallback' | 'reconnecting' | 'visit_stream_ended';
  audio_recovered: boolean;
}

/** A streaming utterance ended with no words; drop its provisional text. */
export interface StreamingProvisionalClearEvent {
  session_id: string;
  segment_id: string;
}

export interface ConversationMessage {
  id: string;
  speaker: 'doctor' | 'patient';
  text: string;
  timestamp?: number;
}

export interface ConversationMessageEvent {
  message: ConversationMessage;
  session_id: string;
  is_reviewed: boolean;
  is_pending_refinement?: boolean; // NEW: True when instant model emitted, waiting for large model refinement
}

export interface ConversationMessageUpdateEvent {
  message_id: string;
  text?: string; // Updated text from large model (if different)
  speaker?: string; // Current speaker (may have been corrected by AI review)
  is_pending_refinement: boolean; // False when refinement complete
  was_refined?: boolean; // True if text was changed by large model
  session_id: string;
}

export interface ConversationReviewEvent {
  messages: ConversationMessage[];
  session_id: string;
  is_final: boolean;
  corrections_count: number;
}

export interface RawTranscriptionEvent {
  session_id: string;
  text: string;
  chunk_number: number;
  start_time: number;
  end_time: number;
}

export interface ConversationSummaryEvent {
  session_id: string;
  summary: string;
  template_used:
    | {
        baseTemplate: string;
        clinicalTemplate: string | null;
      }
    | string
    | null;
  template_id?: number | null;
  /** AI generation id to echo on save/copy; absent from older servers. */
  generation_id?: string | null;
  processing_time_ms: number;
  attributed_summary?: AttributedSummaryData | null;
}

export interface TranscriptionStatusEvent {
  status: 'listening' | 'transcribing' | 'idle';
  session_id: string;
  accumulated_duration?: number;
  target_duration?: number;
}

export interface SmartReviewStartEvent {
  session_id: string;
  segment_count: number;
}

export interface SmartReviewSpeakerTurn {
  segment_ids: string[];
  speaker: string;
  text: string;
  timestamp?: number; // Interpolated start time (for split segments)
  end_timestamp?: number; // Interpolated end time
}

export interface SmartReviewCompleteEvent {
  session_id: string;
  speaker_turns: SmartReviewSpeakerTurn[];
  replaced_segment_ids?: string[];
  full_cleaned_text?: string; // Dictation mode: full accumulated cleaned text (replace, don't append)
  mode: 'conversation' | 'dictation';
  processing_time_ms: number;
}

export interface SmartReviewErrorEvent {
  session_id: string;
  error: string;
}

interface RejoinSessionResponse {
  status?: string;
  message?: string;
  session_id?: string;
  mode?: 'dictation' | 'conversation';
  last_acked_samples?: number;
  smart_review_in_progress?: boolean;
  recovered_smart_review?: SmartReviewCompleteEvent;
  audio_quality?: AudioQualityEvent | null;
}

export interface DetectedTaskEvent {
  type: string;
  title: string;
  details: string;
  confidence: number;
  db_id?: number;
  extra_data?: Record<string, unknown>;
}

export interface TasksDetectedEvent {
  session_id: string;
  tasks: DetectedTaskEvent[];
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface ConnectionEvent {
  state: ConnectionState;
  reason?: string;
  attemptNumber?: number;
  maxAttempts?: number;
  wasClean?: boolean;
  /** The session is over and will not come back. Stop capturing. */
  terminal?: boolean;
  /** Audio already sent may never have become text. Never claim it was kept. */
  tailMayBeIncomplete?: boolean;
}

export class StreamingClient {
  private socket: Socket | null = null;
  private visitGeneration: number | null = null;
  private config: StreamingClientConfig;
  private sessionId: string | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private sessionReady = false;
  /**
   * Set once the session has ended for good. Reconnecting cannot recover it.
   * One-way, and deliberately never reset: every caller builds a fresh client
   * per recording, so a terminated one is discarded rather than reused.
   */
  private terminated = false;
  private failedChunkCount = 0;
  private lastSuccessfulChunkTime = 0;
  private totalChunksSent = 0;
  private pendingRetransmit: Array<{
    pcmArrayBuffer: ArrayBuffer;
    metadata: Record<string, unknown>;
  }> = [];
  private retransmitInterval: ReturnType<typeof setInterval> | null = null;
  private lastSessionConfig: {
    language: string;
    patientId?: string;
    mode: 'dictation' | 'conversation';
    options?: Record<string, unknown>;
  } | null = null;

  private onTranscriptionCallback?: (result: TranscriptionResult) => void;
  private onTranscriptionUpdateCallback?: (event: TranscriptionUpdateEvent) => void;
  private onConversationMessageCallback?: (event: ConversationMessageEvent) => void;
  private onConversationMessageUpdateCallback?: (event: ConversationMessageUpdateEvent) => void;
  private onConversationReviewCallback?: (event: ConversationReviewEvent) => void;
  private onRawTranscriptionCallback?: (event: RawTranscriptionEvent) => void;
  private onConversationSummaryCallback?: (event: ConversationSummaryEvent) => void;
  private onTranscriptionStatusCallback?: (event: TranscriptionStatusEvent) => void;
  private onSmartReviewStartCallback?: (event: SmartReviewStartEvent) => void;
  private onSmartReviewCompleteCallback?: (event: SmartReviewCompleteEvent) => void;
  private onSmartReviewErrorCallback?: (event: SmartReviewErrorEvent) => void;
  private onTasksDetectedCallback?: (event: TasksDetectedEvent) => void;
  private onStreamingWarningCallback?: (event: StreamingWarningEvent) => void;
  private onStreamingProvisionalClearCallback?: (event: StreamingProvisionalClearEvent) => void;
  private onConnectionStateChangeCallback?: (event: ConnectionEvent) => void;
  private onAudioQualityCallback?: (event: AudioQualityEvent) => void;

  constructor(config: StreamingClientConfig = {}) {
    this.config = {
      url: config.url ?? '',
      namespace: config.namespace ?? '/stream',
      authToken: config.authToken,
      embedSession: config.embedSession,
    };
  }

  /**
   * Get current session ID
   */
  /** Shared authenticated Socket.IO connection for the Interpreter adapter. */
  getSocket(): Socket | null {
    return this.socket;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get count of chunks waiting for retransmission (for diagnostics/testing)
   */
  getPendingRetransmitCount(): number {
    return this.pendingRetransmit.length;
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = this.config.url ?? '';
      const namespace = this.config.namespace ?? '/stream';

      this.setConnectionState('connecting');

      this.socket = io(url + namespace, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Number.POSITIVE_INFINITY,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        ...(this.config.authToken
          ? {
              auth: {
                token: this.config.authToken,
                // Only present when there is one. Naming the field selects the
                // server's strict branch, so sending it empty would turn a
                // token-only socket into a failed pair.
                ...(this.config.embedSession ? { session: this.config.embedSession } : {}),
              },
            }
          : {}),
      });

      this.socket.on('connect', () => {
        console.info('[StreamingClient] Connected, socket id:', this.socket?.id);
        this.failedChunkCount = 0;

        if (this.connectionState === 'reconnecting') {
          this.sessionReady = false;
          this.setConnectionState('connected', 'reconnected');
          this.attemptRejoinSession();
        } else {
          this.setConnectionState('connected');
          resolve();
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('[StreamingClient] Connection error:', error);
        if (this.connectionState === 'connecting') {
          reject(error);
          return;
        }
        // socket.active is false only when the SERVER refused the connection; a
        // transport failure leaves it true and socket.io retries on its own.
        // Without this an auth rejection on reconnect was indistinguishable from
        // a blip, and reconnectionAttempts is Infinity: the client retried for
        // ever, buffering audio, with no accepted socket left for the server to
        // deliver a disconnect on.
        if (this.socket && !this.socket.active) {
          this.markTerminated('authentication');
        }
      });

      this.socket.on('session_terminated', (data: { reason?: string; tail_may_be_incomplete?: boolean }) => {
        // Sent as the server begins closing, not when it finishes. Finalisation
        // waits on ASR and review, and for all that time the socket still looks
        // connected while every chunk is refused.
        this.markTerminated(data?.reason ?? 'session_terminated', data?.tail_may_be_incomplete ?? true);
      });

      this.socket.on('disconnect', (reason) => {
        console.warn(`[StreamingClient] Disconnected: ${reason}, sessionId: ${this.sessionId}`);
        // 'io client disconnect' means we called socket.disconnect() intentionally
        // Don't try to reconnect or show error toasts for intentional disconnects
        // socket.io does not reconnect after the SERVER closes the namespace, and
        // it drops its manager subscriptions, so no reconnect attempt and no
        // connect_error ever follow. Treating it as reconnectable left the client
        // buffering for ever with nothing able to wake it -- which is exactly what
        // happens when the terminal notice fails to send and only the disconnect
        // arrives.
        if (reason === 'io server disconnect') {
          this.markTerminated(reason);
        } else if (reason === 'io client disconnect' || this.terminated) {
          this.setConnectionState('disconnected', reason);
        } else {
          this.sessionReady = false;
          this.setConnectionState('reconnecting', reason);
        }
      });

      this.socket.io.on('reconnect_attempt', (attempt: number) => {
        console.warn(`[StreamingClient] Reconnection attempt ${attempt}`);
        this.emitConnectionEvent({
          state: 'reconnecting',
          attemptNumber: attempt,
        });
      });

      this.socket.io.on('reconnect', (attempt: number) => {
        console.info(`[StreamingClient] Reconnected after ${attempt} attempts`);
      });

      this.socket.io.on('reconnect_failed', () => {
        console.error('[StreamingClient] All reconnection attempts failed');
        this.setConnectionState('disconnected', 'reconnect_failed');
      });

      const qualitySocket = this.socket;
      qualitySocket.on('audio_quality', (event: AudioQualityEvent) => {
        if (
          this.socket === qualitySocket &&
          !this.terminated &&
          this.sessionId &&
          event?.session_id === this.sessionId
        ) {
          this.onAudioQualityCallback?.(event);
        }
      });

      // Listen for transcription results (dictation mode)
      this.socket.on('transcription', (data: TranscriptionResult) => {
        if (this.onTranscriptionCallback) {
          this.onTranscriptionCallback(data);
        }
      });

      // Listen for transcription updates (dictation mode - text refinement from large model)
      this.socket.on('transcription_update', (data: TranscriptionUpdateEvent) => {
        if (this.onTranscriptionUpdateCallback) {
          this.onTranscriptionUpdateCallback(data);
        }
      });

      // Listen for conversation messages (conversation mode)
      this.socket.on('conversation_message', (data: ConversationMessageEvent) => {
        if (this.onConversationMessageCallback) {
          this.onConversationMessageCallback(data);
        }
      });

      // Listen for conversation message updates (text refinement from large model)
      this.socket.on('conversation_message_update', (data: ConversationMessageUpdateEvent) => {
        if (this.onConversationMessageUpdateCallback) {
          this.onConversationMessageUpdateCallback(data);
        }
      });

      // Listen for conversation review (speaker corrections)
      this.socket.on('conversation_review', (data: ConversationReviewEvent) => {
        if (this.onConversationReviewCallback) {
          this.onConversationReviewCallback(data);
        }
      });

      // Listen for raw transcription (for admin debugging)
      this.socket.on('raw_transcription', (data: RawTranscriptionEvent) => {
        if (this.onRawTranscriptionCallback) {
          this.onRawTranscriptionCallback(data);
        }
      });

      // Listen for summary generating status
      // Listen for conversation summary
      this.socket.on('conversation_summary', (data: ConversationSummaryEvent) => {
        if (this.onConversationSummaryCallback) {
          this.onConversationSummaryCallback(data);
        }
      });

      // Listen for transcription status (listening/transcribing/idle)
      this.socket.on('transcription_status', (data: TranscriptionStatusEvent) => {
        if (this.onTranscriptionStatusCallback) {
          this.onTranscriptionStatusCallback(data);
        }
      });

      // Listen for smart review events
      this.socket.on('smart_review_start', (data: SmartReviewStartEvent) => {
        if (this.onSmartReviewStartCallback) {
          this.onSmartReviewStartCallback(data);
        }
      });

      this.socket.on('smart_review_complete', (data: SmartReviewCompleteEvent) => {
        if (this.onSmartReviewCompleteCallback) {
          this.onSmartReviewCompleteCallback(data);
        }
      });

      this.socket.on('smart_review_error', (data: SmartReviewErrorEvent) => {
        console.error('[StreamingClient] Smart review error:', data.error);
        if (this.onSmartReviewErrorCallback) {
          this.onSmartReviewErrorCallback(data);
        }
      });

      this.socket.on('tasks_detected', (data: TasksDetectedEvent) => {
        if (this.onTasksDetectedCallback) {
          this.onTasksDetectedCallback(data);
        }
      });

      this.socket.on('streaming_transcription_warning', (data: StreamingWarningEvent) => {
        this.onStreamingWarningCallback?.(data);
      });

      this.socket.on('streaming_provisional_clear', (data: StreamingProvisionalClearEvent) => {
        this.onStreamingProvisionalClearCallback?.(data);
      });

      // Listen for errors
      this.socket.on('error', (error: unknown) => {
        console.error('[StreamingClient] Socket error:', error);
      });
    });
  }

  /**
   * Start a new streaming session
   */
  async startSession(
    sessionId: string,
    language = 'is',
    patientId?: string,
    mode: 'dictation' | 'conversation' = 'dictation',
    options?: {
      doctorName?: string;
      patientName?: string;
      clinicalTemplateId?: number;
      fhirPatientId?: string;
      enableRuleBasedNormalization?: boolean;
      summaryLanguage?: string;
      asrModelPreference?: string | null;
      streamingDictation?: boolean;
      startReason?: 'new' | 'resume' | 'reload';
    }
  ): Promise<void> {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    this.sessionId = sessionId;
    this.lastSessionConfig = { language, patientId, mode, options: options as Record<string, unknown> };

    return new Promise((resolve, reject) => {
      this.socket?.emit(
        'start_session',
        {
          session_id: sessionId,
          language,
          patient_id: patientId,
          mode,
          doctor_name: options?.doctorName,
          patient_name: options?.patientName,
          clinical_template_id: options?.clinicalTemplateId,
          fhir_patient_id: options?.fhirPatientId,
          enable_rule_based_normalization: options?.enableRuleBasedNormalization,
          summary_language: options?.summaryLanguage,
          asr_model_preference: options?.asrModelPreference || undefined,
          streaming_dictation: options?.streamingDictation || undefined,
          start_reason: options?.startReason ?? 'new',
        },
        (response: { status?: string; message?: string }) => {
          if (response?.status === 'success') {
            this.sessionReady = true;
            resolve();
          } else {
            console.error('[StreamingClient] Failed to start session:', response);
            reject(new Error(response?.message || 'Failed to start session'));
          }
        }
      );
    });
  }

  /** Call adapters use the existing authenticated /stream socket and clinical session. */
  async startVisitStream(visitId: string): Promise<number> {
    const response = await this.visitCommand('visit_stream_start', { visit_id: visitId });
    if (typeof response.generation !== 'number') throw new Error('Invalid call stream');
    this.visitGeneration = response.generation;
    return response.generation;
  }

  sendVisitPCM(role: 'doctor' | 'patient', generation: number, chunk: PCMChunk): void {
    if (!this.socket?.connected || !this.sessionReady) throw new Error('Call stream disconnected');
    this.socket.emit('visit_audio_chunk', chunk.pcmArrayBuffer, {
      session_id: this.sessionId,
      role,
      generation,
      sample_rate: chunk.sampleRate,
    });
  }

  async stopVisitStream(abort = false): Promise<void> {
    await this.visitCommand('visit_stream_stop', { abort, generation: this.visitGeneration });
  }

  private async visitCommand(event: string, data: Record<string, unknown>): Promise<{ generation?: number }> {
    if (!this.socket?.connected || !this.sessionReady) throw new Error('Call stream disconnected');
    const response = await this.socket.timeout(45000).emitWithAck(event, { ...data, session_id: this.sessionId });
    if (response?.status !== 'success') throw new Error('Call stream unavailable');
    return response;
  }

  /**
   * Send PCM audio chunk
   */
  async sendPCMChunk(chunk: PCMChunk): Promise<void> {
    if (!this.sessionId) {
      throw new Error('No active session');
    }

    if (this.terminated) {
      // Dropped rather than queued. Nothing will drain this queue: the session
      // is gone, and holding the audio only pretends otherwise.
      return;
    }

    if (!this.socket || !this.socket.connected || !this.sessionReady) {
      this.failedChunkCount++;
      this.queueChunkForRetransmit(chunk);

      if (this.failedChunkCount <= 3 || this.failedChunkCount % 20 === 0) {
        const disconnectReason =
          !this.socket || !this.socket.connected ? 'socket disconnected' : 'session recovery in progress';
        console.warn(
          `[StreamingClient] Cannot send chunk - ${disconnectReason} (failed: ${this.failedChunkCount}, queued: ${this.pendingRetransmit.length}, state: ${this.connectionState})`
        );
      }

      // During reconnect/rejoin, keep buffering quietly until the session is attached again.
      if (this.connectionState === 'reconnecting' || !this.sessionReady) {
        return;
      }

      throw new Error('Socket not connected');
    }

    const metadata = {
      session_id: this.sessionId,
      incrementalDuration: chunk.incrementalDuration,
      totalDuration: chunk.totalDuration,
      totalSamples: chunk.totalSamples,
      sampleRate: chunk.sampleRate,
      channels: chunk.channels,
      bitDepth: chunk.bitDepth,
      captureEndSample: chunk.captureEndSample,
      captureEpochId: chunk.captureEpochId,
    };

    return new Promise((resolve, reject) => {
      this.socket?.emit(
        'audio_chunk',
        chunk.pcmArrayBuffer,
        metadata,
        (response: { status?: string; message?: string }) => {
          if (response?.status === 'success') {
            this.lastSuccessfulChunkTime = Date.now();
            this.totalChunksSent++;
            if (this.failedChunkCount > 0) {
              console.info(`[StreamingClient] Chunk send recovered after ${this.failedChunkCount} failures`);
              this.failedChunkCount = 0;
            }
            resolve();
          } else {
            this.failedChunkCount++;
            if (this.failedChunkCount <= 3 || this.failedChunkCount % 20 === 0) {
              console.error(`[StreamingClient] Failed to send chunk (${this.failedChunkCount}):`, response);
            }
            reject(new Error(response?.message || 'Failed to send chunk'));
          }
        }
      );
    });
  }

  /**
   * Stop the current session
   * @param summaryLanguage Optional language override for the final summary (uses session default if not provided)
   */
  async stopSession(summaryLanguage?: string): Promise<void> {
    if (!this.socket || !this.socket.connected) {
      return;
    }

    if (!this.sessionId) {
      return;
    }

    return new Promise((resolve) => {
      this.socket?.emit(
        'stop_session',
        {
          session_id: this.sessionId,
          summary_language: summaryLanguage,
        },
        (_response: { status?: string; message?: string }) => {
          this.sessionId = null;
          this.sessionReady = false;
          this.pendingRetransmit = [];
          if (this.retransmitInterval) {
            clearInterval(this.retransmitInterval);
            this.retransmitInterval = null;
          }
          resolve();
        }
      );
    });
  }

  /**
   * Flush the audio buffer without ending the session (for pause/resume)
   * This triggers Phase 2 transcription on any pending audio
   */
  async flushBuffer({ detectTasks = true } = {}): Promise<void> {
    if (!this.socket || !this.socket.connected) {
      return;
    }

    if (!this.sessionId) {
      return;
    }

    return new Promise((resolve) => {
      this.socket?.emit(
        'flush_buffer',
        {
          session_id: this.sessionId,
          detect_tasks: detectTasks,
        },
        (_response: { status?: string; message?: string }) => {
          resolve();
        }
      );
    });
  }

  /**
   * Set callback for transcription results (dictation mode)
   */
  onTranscription(callback: (result: TranscriptionResult) => void): void {
    this.onTranscriptionCallback = callback;
  }

  /**
   * Set callback for transcription updates (dictation mode - text refinement from large model)
   */
  onTranscriptionUpdate(callback: (event: TranscriptionUpdateEvent) => void): void {
    this.onTranscriptionUpdateCallback = callback;
  }

  /**
   * Set callback for conversation messages (conversation mode)
   */
  onConversationMessage(callback: (event: ConversationMessageEvent) => void): void {
    this.onConversationMessageCallback = callback;
  }

  /**
   * Set callback for conversation message updates (text refinement from large model)
   */
  onConversationMessageUpdate(callback: (event: ConversationMessageUpdateEvent) => void): void {
    this.onConversationMessageUpdateCallback = callback;
  }

  /**
   * Set callback for conversation review (speaker corrections)
   */
  onConversationReview(callback: (event: ConversationReviewEvent) => void): void {
    this.onConversationReviewCallback = callback;
  }

  /**
   * Set callback for raw transcription (for admin debugging)
   */
  onRawTranscription(callback: (event: RawTranscriptionEvent) => void): void {
    this.onRawTranscriptionCallback = callback;
  }

  /**
   * Set callback for conversation summary
   */
  onConversationSummary(callback: (event: ConversationSummaryEvent) => void): void {
    this.onConversationSummaryCallback = callback;
  }

  /**
   * Set callback for transcription status (listening/transcribing/idle)
   */
  onTranscriptionStatus(callback: (event: TranscriptionStatusEvent) => void): void {
    this.onTranscriptionStatusCallback = callback;
  }

  /**
   * Set callback for smart review start
   */
  onSmartReviewStart(callback: (event: SmartReviewStartEvent) => void): void {
    this.onSmartReviewStartCallback = callback;
  }

  /**
   * Set callback for smart review completion
   */
  onSmartReviewComplete(callback: (event: SmartReviewCompleteEvent) => void): void {
    this.onSmartReviewCompleteCallback = callback;
  }

  /**
   * Set callback for smart review errors
   */
  onSmartReviewError(callback: (event: SmartReviewErrorEvent) => void): void {
    this.onSmartReviewErrorCallback = callback;
  }

  /**
   * Set callback for server-side task detection results
   */
  onTasksDetected(callback: (event: TasksDetectedEvent) => void): void {
    this.onTasksDetectedCallback = callback;
  }

  /**
   * Set callback for streaming dictation warnings (failed final, fallback, dropped audio)
   */
  onStreamingWarning(callback: (event: StreamingWarningEvent) => void): void {
    this.onStreamingWarningCallback = callback;
  }

  /**
   * Set callback for a streaming utterance that ended with no words
   */
  onStreamingProvisionalClear(callback: (event: StreamingProvisionalClearEvent) => void): void {
    this.onStreamingProvisionalClearCallback = callback;
  }

  /**
   * Request generation of conversation summary
   * Only works in conversation mode
   * @param templateId Optional clinical template ID to use for summary generation
   * @param regenTrigger Optional hint for why this regeneration ran (template | retry)
   */
  async generateSummary(
    templateId?: number | null,
    style?: SummaryStyle,
    regenTrigger?: RegenTriggerHint
  ): Promise<string> {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    if (!this.sessionId) {
      throw new Error('No active session');
    }

    return new Promise((resolve, reject) => {
      this.socket?.emit(
        'generate_summary',
        {
          session_id: this.sessionId,
          clinical_template_id: templateId ?? undefined,
          style,
          regen_trigger: regenTrigger,
        },
        (response: { status?: string; message?: string; summary?: string }) => {
          if (response?.status === 'success') {
            resolve(response.summary ?? '');
          } else {
            console.error('[StreamingClient] Failed to generate summary:', response);
            reject(new Error(response?.message || 'Failed to generate summary'));
          }
        }
      );
    });
  }

  /**
   * Attempt to rejoin an existing session after reconnection.
   * The server keeps the session alive for 60s after disconnect.
   */
  private attemptRejoinSession(): void {
    if (!this.socket || !this.sessionId) return;

    this.sessionReady = false;
    console.info(`[StreamingClient] Attempting to rejoin session ${this.sessionId.slice(0, 12)}...`);

    this.socket.emit('rejoin_session', { session_id: this.sessionId }, (response: RejoinSessionResponse) => {
      if (response?.status === 'success') {
        console.info('[StreamingClient] Session rejoined successfully:', response);
        this.sessionReady = true;
        this.failedChunkCount = 0;
        this.syncSmartReviewStateAfterRejoin(response);
        if (!this.terminated && response.audio_quality?.session_id === this.sessionId) {
          this.onAudioQualityCallback?.(response.audio_quality);
        }
        this.emitConnectionEvent({ state: 'connected', reason: 'session_rejoined' });

        // Drain retransmit queue — send chunks the server missed
        this.drainRetransmitQueue(response.last_acked_samples ?? 0);
      } else {
        console.warn('[StreamingClient] Rejoin failed:', response?.message);
        this.sessionReady = false;
        // Clear retransmit queue — session is gone, nothing to send to
        this.pendingRetransmit = [];
        // Clear session and disconnect socket to stop sending audio chunks
        // to a session that no longer exists (prevents server-side spam storm)
        this.sessionId = null;
        this.socket?.disconnect();
        this.emitConnectionEvent({
          state: 'disconnected',
          reason: `rejoin_failed: ${response?.message}`,
        });
      }
    });
  }

  private queueChunkForRetransmit(chunk: PCMChunk): void {
    this.pendingRetransmit.push({
      pcmArrayBuffer: chunk.pcmArrayBuffer,
      metadata: {
        session_id: this.sessionId,
        incrementalDuration: chunk.incrementalDuration,
        totalDuration: chunk.totalDuration,
        totalSamples: chunk.totalSamples,
        sampleRate: chunk.sampleRate,
        channels: chunk.channels,
        bitDepth: chunk.bitDepth,
        // Must survive the queue. A replayed chunk that arrives without its
        // capture stamps is stamped from the received clock instead, which
        // reintroduces the drift for exactly the audio an outage delayed, and
        // cannot be deduped — a synthesized offset carries no identity, so
        // audio the server already holds would be appended twice.
        captureEndSample: chunk.captureEndSample,
        captureEpochId: chunk.captureEpochId,
      },
    });
  }

  private syncSmartReviewStateAfterRejoin(response: RejoinSessionResponse): void {
    if (response.smart_review_in_progress) {
      this.onSmartReviewStartCallback?.({
        session_id: response.session_id ?? this.sessionId ?? '',
        segment_count: 0,
      });
      return;
    }

    if (response.recovered_smart_review) {
      this.onSmartReviewCompleteCallback?.(response.recovered_smart_review);
      return;
    }

    this.onSmartReviewCompleteCallback?.({
      session_id: response.session_id ?? this.sessionId ?? '',
      speaker_turns: [],
      mode: response.mode ?? this.lastSessionConfig?.mode ?? 'dictation',
      processing_time_ms: 0,
    });
  }

  /**
   * Drain the retransmit queue after a successful rejoin.
   * Filters out chunks already received by the server (by sample count),
   * then sends remaining chunks at ~4/sec to avoid event loop starvation.
   */
  private drainRetransmitQueue(lastAckedSamples: number): void {
    // Clear any existing drain interval (rapid reconnect could start a second one)
    if (this.retransmitInterval) {
      clearInterval(this.retransmitInterval);
      this.retransmitInterval = null;
    }

    const toRetransmit = this.pendingRetransmit.filter(
      (entry) => (entry.metadata.totalSamples as number) > lastAckedSamples
    );

    const skipped = this.pendingRetransmit.length - toRetransmit.length;
    this.pendingRetransmit = [];

    if (toRetransmit.length === 0) {
      if (skipped > 0) {
        console.info(`[StreamingClient] Retransmit: all ${skipped} queued chunks already acked`);
      }
      return;
    }

    console.info(`[StreamingClient] Retransmitting ${toRetransmit.length} chunks (skipped ${skipped} already acked)`);

    let index = 0;
    this.retransmitInterval = setInterval(() => {
      if (index >= toRetransmit.length || !this.socket?.connected) {
        if (this.retransmitInterval) {
          clearInterval(this.retransmitInterval);
          this.retransmitInterval = null;
        }
        if (index >= toRetransmit.length) {
          console.info(`[StreamingClient] Retransmit complete: ${toRetransmit.length} chunks sent`);
        }
        return;
      }

      const entry = toRetransmit[index];
      const metadata = { ...entry.metadata, retransmitted: true };

      this.socket.emit('audio_chunk', entry.pcmArrayBuffer, metadata, (response: { status?: string }) => {
        if (response?.status !== 'success') {
          console.warn(`[StreamingClient] Retransmit chunk failed (samples=${entry.metadata.totalSamples})`);
        }
      });

      index++;
    }, 250);
  }

  /**
   * End the session for good, without tearing down the socket.
   *
   * The socket is deliberately left alone. The server marks the session closing,
   * tells us, and only then finalises, which can wait on ASR and review. Results
   * for audio it already accepted still arrive over this socket, so the listeners
   * have to outlive the decision to stop capturing.
   *
   * The tail is reported as possibly incomplete rather than lost or kept: the
   * final flush needs credentials the server may no longer have, so neither
   * claim would be honest.
   */
  private markTerminated(reason: string, tailMayBeIncomplete = true): void {
    if (this.terminated) {
      return;
    }
    this.terminated = true;
    this.sessionReady = false;
    this.pendingRetransmit = [];
    // Assigned directly rather than through setConnectionState, which would emit
    // a second, plain disconnect event alongside the terminal one below. Setting
    // it here instead makes the 'disconnect' handler's own call a no-op, so the
    // consumer sees exactly one event and it is the one carrying the flags.
    this.connectionState = 'disconnected';
    console.warn(`[StreamingClient] Session terminated (${reason}); stopping capture`);
    this.emitConnectionEvent({
      state: 'disconnected',
      reason,
      terminal: true,
      // The server's answer, not ours. It knows whether its final flush could
      // still reach ASR; we only know the session ended. Defaults to true for
      // the paths where no notice arrived at all, which is the honest reading
      // when nobody told us the tail survived.
      tailMayBeIncomplete,
    });
  }

  private setConnectionState(state: ConnectionState, reason?: string): void {
    const prev = this.connectionState;
    this.connectionState = state;
    if (prev !== state) {
      console.info(`[StreamingClient] Connection state: ${prev} -> ${state}${reason ? ` (${reason})` : ''}`);
      this.emitConnectionEvent({ state, reason });
    }
  }

  private emitConnectionEvent(event: ConnectionEvent): void {
    if (this.onConnectionStateChangeCallback) {
      this.onConnectionStateChangeCallback(event);
    }
  }

  /**
   * Set callback for connection state changes (disconnect, reconnect, etc.)
   */
  onConnectionStateChange(callback: (event: ConnectionEvent) => void): void {
    this.onConnectionStateChangeCallback = callback;
  }

  onAudioQuality(callback: (event: AudioQualityEvent) => void): void {
    this.onAudioQualityCallback = callback;
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Get count of chunks that failed to send since last successful send
   */
  getFailedChunkCount(): number {
    return this.failedChunkCount;
  }

  /**
   * Get chunk delivery health metrics for pipeline monitoring.
   */
  getChunkHealth(): {
    lastChunkAgeMs: number;
    chunksSent: number;
    chunksFailed: number;
    connState: ConnectionState;
  } {
    return {
      lastChunkAgeMs: this.lastSuccessfulChunkTime ? Date.now() - this.lastSuccessfulChunkTime : -1,
      chunksSent: this.totalChunksSent,
      chunksFailed: this.failedChunkCount,
      connState: this.connectionState,
    };
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    this.pendingRetransmit = [];
    this.sessionReady = false;
    if (this.retransmitInterval) {
      clearInterval(this.retransmitInterval);
      this.retransmitInterval = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.sessionId = null;
    this.lastSessionConfig = null;
    this.setConnectionState('disconnected', 'manual');
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}
